import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { SectionLabel } from '../components/SectionLabel';
import { Bloom } from '../components/Bloom';
import { CTAButton } from '../components/CTAButton';
import { SEOHead } from '../components/SEOHead';
const faq = [{
  question: 'How long does setup take?',
  answer: 'Most clients are fully live in about a week.'
}, {
  question: 'What do I have to do myself?',
  answer: 'A self-serve onboarding portal walks you through your login, the mobile app, syncing your calendar, a CRM tour, adding your team, and connecting your socials.'
}, {
  question: 'What does Viridia Analytics set up for me?',
  answer: 'On a scheduled call we build your AI receptionist in your brand voice, set up your business phone number and A2P registration, and build your workflows with you.'
}];
const selfServe = [{
  title: 'Get your login settled',
  body: 'Set your password and you are in.'
}, {
  title: 'Download the app',
  body: 'The whole system on your phone, not just the desk.'
}, {
  title: 'Sync your calendar',
  body: 'So booked estimates land where you already look.'
}, {
  title: 'Take the CRM tour',
  body: 'A quick walkthrough of where everything lives.'
}, {
  title: 'Add your team',
  body: 'Give your crew or office manager their own users.'
}, {
  title: 'Connect your socials',
  body: 'Link your accounts so posting and messages come through.'
}];

/* 2026-09-14 (compliance, Kevin's standing rule, CLAUDE.md): this item used
   to read "Follow-ups, reminders, and review requests built with you on the
   call, for your services." Yelp has already flagged Viridia twice for
   review-related site copy. "Review requests" as a workflow feature is
   exactly the banned claim, and this page was live and routed the whole
   time -- missed by both prior compliance sweeps (2026-09-08, 2026-09-10),
   which is the "don't rely on remembering which pages were already cleaned"
   scenario CLAUDE.md warns about. Trimmed to the real, already-claimed
   features (follow-ups, reminders -- see systemSteps in data/site.ts). Do
   not reinstate review-request copy here or anywhere else on the site. */
const withUs = [{
  title: 'Your AI receptionist',
  body: 'We build and tune it in your brand voice: how you answer, what you quote, what you never say.'
}, {
  title: 'Your business phone number',
  body: 'Set up so calls and texts run through the system instead of your personal cell.'
}, {
  title: 'A2P registration',
  body: 'The carrier paperwork that keeps your texts from getting filtered. We handle it.'
}, {
  title: 'Your workflows',
  body: 'Follow-ups and reminders built with you on the call, for your services.'
}];
export function GettingStarted() {
  return <>
      <SEOHead title="Getting Started | Easy Onboarding + White-Glove Setup | Viridia Analytics" description="You handle the easy part in our onboarding portal; we build your AI receptionist, phone number, and workflows with you. Most clients live in about a week." canonical="/getting-started" faq={faq} data-id="element-2661" data-mp="2becin417u5pt" />

      <PageHeader label="Getting started" title="Getting started is the easy part." intro="You're not handed a login and left to figure it out. Setup splits in two: the simple stuff you knock out yourself in an onboarding portal, and the technical, in-your-voice stuff we build with you on a call." data-id="element-2662" data-mp="2bmzanhvj06wj" />

      <section className="relative isolate overflow-hidden border-b border-[#1a1a1a] bg-[#0c0c0c]" data-id="element-2663" data-mp="2ujiogkh9yizf">
        <Bloom size="md" className="left-0 top-[-6rem]" data-id="element-2664" data-mp="2qif7funhqgwm" />
        <div className="mx-auto w-full max-w-6xl px-6 py-20 lg:py-24" data-id="element-2665" data-mp="2qhx5qii27ahb">
          <SectionLabel data-id="element-2666" data-mp="3n13ctbc9p1co">Your part</SectionLabel>
          <h2 className="mt-8 max-w-2xl text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-2667" data-mp="3n1wg22c0p8q2">
            You handle the easy part.
          </h2>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-400" data-id="element-2668" data-mp="32pquldxhaknt">
            A portal walks you through it, one step at a time. Knock it out between jobs.
          </p>

          <ol className="mt-12 grid grid-cols-1 gap-x-10 gap-y-8 sm:grid-cols-2 lg:grid-cols-3" data-id="element-2669" data-mp="2tryp11hk6rdm">
            {selfServe.map((s, i) => <motion.li key={s.title} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.4,
            delay: i * 0.05
          }} className="border-t border-[#1c1c1c] pt-5" data-id="element-2670" data-mp="2xu237xvm4a2l" data-mpi={i}>
                <p className="font-mono text-xs tracking-[0.2em] text-neutral-600" data-id="element-2671" data-mp="3gq1mmkaiy5ak">
                  {String(i + 1).padStart(2, '0')}
                </p>
                <h3 className="mt-3 text-base font-semibold text-white" data-id="element-2672" data-mp="d1zx2h39h1k6">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-400" data-id="element-2673" data-mp="d448dk3hp2uh">{s.body}</p>
              </motion.li>)}
          </ol>
        </div>
      </section>

      <section className="relative isolate overflow-hidden border-b border-[#1a1a1a]" data-id="element-2674" data-mp="1sssqyiw3ifgq">
        <Bloom size="md" className="right-0 top-[-6rem]" data-id="element-2675" data-mp="27h6tsttii3xa" />
        <div className="mx-auto w-full max-w-6xl px-6 py-20 lg:py-24" data-id="element-2676" data-mp="22j5cadbqicnx">
          <SectionLabel data-id="element-2677" data-mp="22l885dzkj00k">Our part</SectionLabel>
          <h2 className="mt-8 max-w-2xl text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-2678" data-mp="1esre052k3hkw">
            We handle the rest, with you.
          </h2>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-400" data-id="element-2679" data-mp="1orqozuasnpb2">
            The parts that have to sound like you get built on a call, with you on the line.
          </p>

          <ul className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-[#1c1c1c] bg-[#1c1c1c] sm:grid-cols-2" data-id="element-2680" data-mp="1jv0b9dmgp5st">
            {withUs.map(w => <li key={w.title} className="bg-[#0d0d0d] p-8" data-id="element-2681" data-mp="2v7arylnxlbgw">
                <h3 className="text-base font-semibold text-white" data-id="element-2682" data-mp="32bsghgw0dbjj">{w.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-400" data-id="element-2683" data-mp="379txzxdsd2sw">{w.body}</p>
              </li>)}
          </ul>

          <p className="mt-10 max-w-2xl text-lg leading-relaxed text-neutral-300" data-id="element-2684" data-mp="3ca861ffe0cqx">
            You do the easy part. We bring the brand, the voice, and the technical setup.
          </p>
        </div>
      </section>

      <section className="relative isolate overflow-hidden border-b border-[#1a1a1a]" data-id="element-2685" data-mp="2jm2o10fpx1us">
        <Bloom size="lg" className="left-1/2 top-[-16rem] -translate-x-1/2" data-id="element-2686" data-mp="2okdz1ehr1iel" />
        <div className="mx-auto w-full max-w-3xl px-6 py-24 text-center lg:py-28" data-id="element-2687" data-mp="2tifdtn856vk9">
          <h2 className="text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-2688" data-mp="2y95nhcnlpkkr">
            Most clients are fully live in about a week.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-neutral-400" data-id="element-2689" data-mp="20s766pb8ftjx">
            Book the setup call and we will get the calendar sorted on the same call.
          </p>
          <div className="mt-9 flex justify-center" data-id="element-2690" data-mp="mvw2qq3l1qt9">
            <CTAButton to="/contact" data-id="element-2691" data-mp="iw55q4ngqega">Book your setup call</CTAButton>
          </div>
          <p className="mt-6 flex items-center justify-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] text-neutral-600" data-id="element-2692" data-mp="dwsivjwld7dp">
            Phoenix metro
            <ArrowRightIcon className="h-3 w-3" aria-hidden="true" data-id="element-2693" data-mp="90bk7vrrnamd" />
            Month to month
          </p>
        </div>
      </section>
    </>;
}